/**
 * Pure, runtime-safe normalization for the browser-facing policy inbox work
 * plan. This module lives outside src/app so both the Node build and the Vite
 * app consume the same allowlisted contract.
 */
export type PolicyInboxSourceKind = "email" | "browser" | "export" | "paste" | "other";
export type PolicyInboxRegistryState = "active" | "paused" | "archived";
export type PolicyInboxWorkState =
  | "running"
  | "repair_required"
  | "never_scanned"
  | "due"
  | "current"
  | "paused";

export interface PolicyInboxWorkPlanLatestScan {
  scan_run_id: string;
  status: "running" | "completed" | "partial" | "failed" | "abandoned";
  requested_from: string | null;
  requested_through: string | null;
  covered_through: string | null;
  last_activity_at: string | null;
  messages_examined: number;
  messages_selected: number;
  messages_persisted: number;
  messages_duplicate: number;
  messages_reconciled: number;
  messages_failed: number;
}

export interface PolicyInboxWorkPlanRunningScan {
  scan_run_id: string;
  requested_from: string;
  requested_through: string;
  last_activity_at: string;
}

export type PolicyInboxWorkPlanAction = {
  action: "resume_or_close_running_scans";
  first_tool: string | null;
  running_scan_count: number;
  scans: PolicyInboxWorkPlanRunningScan[];
  instruction: string | null;
} | {
  action: "begin_scan";
  tool: string | null;
  source_key: string | null;
  source_kind: PolicyInboxSourceKind | null;
  recommended_requested_from: string | null;
  lookback_hours: number;
  overlap_minutes: number;
  client_run_key_requirement: string | null;
};

export interface PolicyInboxWorkPlanSource {
  source_key: string;
  display_name: string;
  source_kind: PolicyInboxSourceKind;
  registry_state: PolicyInboxRegistryState;
  work_state: PolicyInboxWorkState;
  priority: number;
  schedule: {
    cadence_hours: number;
    default_lookback_hours: number;
    overlap_minutes: number;
  };
  coverage: {
    last_successfully_covered_through: string | null;
    unsafe_retry_from: string | null;
    next_due_at: string | null;
    is_current: boolean;
  };
  latest_scan: PolicyInboxWorkPlanLatestScan | null;
  recommended_action: PolicyInboxWorkPlanAction | null;
}

export interface PolicyInboxWorkPlanSummary {
  registered_source_count: number;
  active_source_count: number;
  paused_source_count: number;
  archived_source_count: number;
  running_source_count: number;
  repair_required_source_count: number;
  never_scanned_source_count: number;
  due_source_count: number;
  current_source_count: number;
  coverage_current_source_count: number;
  coverage_percent: number | null;
  unregistered_observed_source_count: number;
}

export interface PolicyInboxWorkPlanData {
  workspace_id: string | null;
  as_of: string | null;
  completeness_scope: "registered_sources_only";
  summary: PolicyInboxWorkPlanSummary;
  sources: PolicyInboxWorkPlanSource[];
  observed_unregistered_sources: Array<{
    source_key: string;
    source_kind: PolicyInboxSourceKind;
    scan_count: number;
    latest_scan_started_at: string | null;
  }>;
  discovery_boundary: string;
  privacy_boundary: string;
}

const SOURCE_KINDS = new Set<string>(["email", "browser", "export", "paste", "other"]);
const REGISTRY_STATES = new Set<string>(["active", "paused", "archived"]);
const WORK_STATES = new Set<string>([
  "running",
  "repair_required",
  "never_scanned",
  "due",
  "current",
  "paused",
]);
const SCAN_STATES = new Set<string>(["running", "completed", "partial", "failed", "abandoned"]);

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function textValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function countValue(value: unknown, max = 1_000_000_000): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0
    ? Math.min(max, Math.trunc(parsed))
    : 0;
}

function normalizeLatestScan(value: unknown): PolicyInboxWorkPlanLatestScan | null {
  const scan = recordValue(value);
  const scanRunId = textValue(scan.scan_run_id);
  const status = textValue(scan.status);
  if (!scanRunId || !status || !SCAN_STATES.has(status)) return null;
  return {
    scan_run_id: scanRunId,
    status: status as PolicyInboxWorkPlanLatestScan["status"],
    requested_from: textValue(scan.requested_from),
    requested_through: textValue(scan.requested_through),
    covered_through: textValue(scan.covered_through),
    last_activity_at: textValue(scan.last_activity_at),
    messages_examined: countValue(scan.messages_examined),
    messages_selected: countValue(scan.messages_selected),
    messages_persisted: countValue(scan.messages_persisted),
    messages_duplicate: countValue(scan.messages_duplicate),
    messages_reconciled: countValue(scan.messages_reconciled),
    messages_failed: countValue(scan.messages_failed),
  };
}

function normalizeRecommendedAction(value: unknown): PolicyInboxWorkPlanAction | null {
  const action = recordValue(value);
  const actionName = textValue(action.action);
  if (actionName === "resume_or_close_running_scans") {
    const scans = (Array.isArray(action.scans) ? action.scans : []).flatMap((value) => {
      const scan = recordValue(value);
      const scanRunId = textValue(scan.scan_run_id);
      const requestedFrom = textValue(scan.requested_from);
      const requestedThrough = textValue(scan.requested_through);
      const lastActivityAt = textValue(scan.last_activity_at);
      if (!scanRunId || !requestedFrom || !requestedThrough || !lastActivityAt) return [];
      return [{
        scan_run_id: scanRunId,
        requested_from: requestedFrom,
        requested_through: requestedThrough,
        last_activity_at: lastActivityAt,
      }];
    }).slice(0, 1);
    return {
      action: actionName,
      first_tool: textValue(action.first_tool),
      running_scan_count: countValue(action.running_scan_count, 1),
      scans,
      instruction: textValue(action.instruction),
    };
  }
  if (actionName === "begin_scan") {
    const sourceKind = textValue(action.source_kind);
    return {
      action: actionName,
      tool: textValue(action.tool),
      source_key: textValue(action.source_key),
      source_kind: sourceKind && SOURCE_KINDS.has(sourceKind) ? sourceKind as PolicyInboxSourceKind : null,
      recommended_requested_from: textValue(action.recommended_requested_from),
      lookback_hours: countValue(action.lookback_hours, 8784),
      overlap_minutes: countValue(action.overlap_minutes, 1440),
      client_run_key_requirement: textValue(action.client_run_key_requirement),
    };
  }
  return null;
}

function normalizeSource(value: unknown): PolicyInboxWorkPlanSource | null {
  const source = recordValue(value);
  const sourceKey = textValue(source.source_key);
  const displayName = textValue(source.display_name);
  const sourceKind = textValue(source.source_kind);
  const registryState = textValue(source.registry_state);
  const workState = textValue(source.work_state);
  if (
    !sourceKey || !displayName || !sourceKind || !registryState || !workState
    || !SOURCE_KINDS.has(sourceKind)
    || !REGISTRY_STATES.has(registryState)
    || !WORK_STATES.has(workState)
  ) return null;
  const schedule = recordValue(source.schedule);
  const coverage = recordValue(source.coverage);
  return {
    source_key: sourceKey,
    display_name: displayName,
    source_kind: sourceKind as PolicyInboxSourceKind,
    registry_state: registryState as PolicyInboxRegistryState,
    work_state: workState as PolicyInboxWorkState,
    priority: Math.min(100, countValue(source.priority, 100)),
    schedule: {
      cadence_hours: countValue(schedule.cadence_hours, 8784),
      default_lookback_hours: countValue(schedule.default_lookback_hours, 8784),
      overlap_minutes: countValue(schedule.overlap_minutes, 1440),
    },
    coverage: {
      last_successfully_covered_through: textValue(coverage.last_successfully_covered_through),
      unsafe_retry_from: textValue(coverage.unsafe_retry_from),
      next_due_at: textValue(coverage.next_due_at),
      is_current: coverage.is_current === true,
    },
    latest_scan: normalizeLatestScan(source.latest_scan),
    recommended_action: normalizeRecommendedAction(source.recommended_action),
  };
}

const EMPTY_SUMMARY: PolicyInboxWorkPlanSummary = {
  registered_source_count: 0,
  active_source_count: 0,
  paused_source_count: 0,
  archived_source_count: 0,
  running_source_count: 0,
  repair_required_source_count: 0,
  never_scanned_source_count: 0,
  due_source_count: 0,
  current_source_count: 0,
  coverage_current_source_count: 0,
  coverage_percent: null,
  unregistered_observed_source_count: 0,
};

export function normalizePolicyInboxWorkPlan(value: unknown): PolicyInboxWorkPlanData {
  const plan = recordValue(value);
  const summary = recordValue(plan.summary);
  const rawCoveragePercent = Number(summary.coverage_percent);
  const coveragePercent = summary.coverage_percent == null || !Number.isFinite(rawCoveragePercent)
    ? null
    : Math.max(0, Math.min(100, rawCoveragePercent));
  return {
    workspace_id: textValue(plan.workspace_id),
    as_of: textValue(plan.as_of),
    completeness_scope: "registered_sources_only",
    summary: {
      ...EMPTY_SUMMARY,
      registered_source_count: countValue(summary.registered_source_count, 100),
      active_source_count: countValue(summary.active_source_count, 100),
      paused_source_count: countValue(summary.paused_source_count, 100),
      archived_source_count: countValue(summary.archived_source_count, 100),
      running_source_count: countValue(summary.running_source_count, 100),
      repair_required_source_count: countValue(summary.repair_required_source_count, 100),
      never_scanned_source_count: countValue(summary.never_scanned_source_count, 100),
      due_source_count: countValue(summary.due_source_count, 100),
      current_source_count: countValue(summary.current_source_count, 100),
      coverage_current_source_count: countValue(summary.coverage_current_source_count, 100),
      coverage_percent: coveragePercent,
      unregistered_observed_source_count: countValue(summary.unregistered_observed_source_count, 100),
    },
    sources: (Array.isArray(plan.sources) ? plan.sources : [])
      .map(normalizeSource)
      .filter((source): source is PolicyInboxWorkPlanSource => source !== null)
      .slice(0, 100),
    observed_unregistered_sources: (
      Array.isArray(plan.observed_unregistered_sources) ? plan.observed_unregistered_sources : []
    ).flatMap((value) => {
      const source = recordValue(value);
      const sourceKey = textValue(source.source_key);
      const sourceKind = textValue(source.source_kind);
      if (!sourceKey || !sourceKind || !SOURCE_KINDS.has(sourceKind)) return [];
      return [{
        source_key: sourceKey,
        source_kind: sourceKind as PolicyInboxSourceKind,
        scan_count: countValue(source.scan_count),
        latest_scan_started_at: textValue(source.latest_scan_started_at),
      }];
    }).slice(0, 100),
    discovery_boundary: textValue(plan.discovery_boundary) ?? "Only registered source labels count toward completeness.",
    privacy_boundary: textValue(plan.privacy_boundary) ?? "The authorized external AI reads the upstream source.",
  };
}
